import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { TimeSlot } from '@/models/Schedule';
import { WorkoutType } from '@/models/WorkoutType';
import { EffectiveBooking } from '@/hooks/useRecurringBookings';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { Clock, Users, Plus, Repeat, UserCheck } from 'lucide-react';
import AddClientDialog from './AddClientDialog';
import EffectiveViewClientsDialog from './EffectiveViewClientsDialog';
import { useIsMobile } from '@/hooks/use-mobile';

interface EffectiveTimeSlotCardProps {
  timeSlot: TimeSlot;
  workoutType: WorkoutType | undefined;
  selectedDate: Date;
  effectiveBookings: EffectiveBooking[];
  isHighlighted?: boolean;
}

const EffectiveTimeSlotCard = ({
  timeSlot,
  workoutType,
  selectedDate,
  effectiveBookings,
  isHighlighted = false,
}: EffectiveTimeSlotCardProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);
  const [showViewClientsDialog, setShowViewClientsDialog] = useState(false);

  const availableSpots = timeSlot.maxClients - effectiveBookings.length;
  const isFull = availableSpots <= 0;

  const handleAddClientSuccess = () => {
    // This will trigger a refetch of the effective bookings
  };

  const handleViewClients = () => {
    if (effectiveBookings.length > 0) {
      setShowViewClientsDialog(true);
    }
  };

  const handleClientClick = (clientId: string) => {
    navigate(`/client/${clientId}`);
  };

  return (
    <>
      <Card
        className={`transition-all duration-200 hover:shadow-md ${
          isHighlighted ? 'ring-2 ring-primary ring-offset-2 shadow-lg' : ''
        }`}
        data-timeslot={timeSlot.startTime}
      >
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5 text-muted-foreground" />
              <span className="font-semibold">
                {normalizeTime(timeSlot.startTime)} -{' '}
                {normalizeTime(timeSlot.endTime)}
              </span>
            </CardTitle>

            {workoutType && (
              <Badge
                variant="secondary"
                className="w-fit"
                style={{
                  backgroundColor: workoutType.color,
                  color: 'white',
                }}
              >
                {workoutType.name}
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Capacity and booking info */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />
              <span>
                {effectiveBookings.length}/{timeSlot.maxClients}{' '}
                {t('calendar.bookedClients')}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {availableSpots > 0 ? (
                <Badge
                  variant="outline"
                  className="text-green-600 border-green-200 bg-green-50"
                >
                  {t('calendar.availableSpots', { count: availableSpots })}
                </Badge>
              ) : (
                <Badge variant="destructive">{t('calendar.sessionFull')}</Badge>
              )}
            </div>
          </div>

          {/* Client list preview */}
          {effectiveBookings.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium">
                {t('calendar.bookedClients')}:
              </div>
              <div className="flex flex-wrap gap-1">
                {(() => {
                  const allParticipants: Array<{
                    type: 'regular' | 'trial';
                    id: string;
                    name: string;
                    booking: EffectiveBooking;
                    index: number;
                  }> = [];

                  // Process each booking
                  effectiveBookings.forEach((booking, index) => {
                    // Check if this is a trial client (client_id is null and payment_status is 'trial')
                    if (!booking.client_id && booking.client_payment_status === 'trial') {
                      allParticipants.push({
                        type: 'trial',
                        id: booking.booking_id || `trial-${index}`, // Use booking_id or fallback
                        name: booking.client_first_name, // For trial clients, full name is stored in client_first_name
                        booking,
                        index,
                      });
                    }
                    // Add regular client if there is one
                    else if (booking.client_id) {
                      allParticipants.push({
                        type: 'regular',
                        id: booking.client_id,
                        name: `${booking.client_first_name} ${booking.client_last_name}`,
                        booking,
                        index,
                      });
                    }
                  });

                  return (
                    <>
                      {allParticipants.map((participant) => {
                        const effectiveStatus =
                          participant.booking.session_consumption_type ||
                          participant.booking.booking_status;
                        const isCompleted = effectiveStatus === 'completed';
                        const isMissed = effectiveStatus === 'missed';
                        const isTrialClient = participant.type === 'trial' || participant.booking.client_payment_status === 'trial';

                        return (
                          <Badge
                            key={`${participant.type}-${participant.id}-${participant.index}`}
                            variant={
                              isTrialClient
                                ? 'outline'
                                : isCompleted
                                ? 'completed'
                                : isMissed
                                ? 'missed'
                                : 'outline'
                            }
                            className={`text-xs flex items-center gap-1 transition-opacity ${
                              isTrialClient
                                ? 'border-blue-300 text-blue-700 bg-blue-50 dark:border-blue-600 dark:text-blue-300 dark:bg-blue-900/20'
                                : 'cursor-pointer hover:opacity-80'
                            }`}
                            onClick={
                              isTrialClient
                                ? undefined
                                : () => handleClientClick(participant.id)
                            }
                          >
                            {isTrialClient && <UserCheck className="h-3 w-3" />}
                            {participant.booking.booking_type === 'recurring' &&
                              !isTrialClient && <Repeat className="h-3 w-3" />}
                            {isCompleted && !isTrialClient && <span>✓</span>}
                            {isMissed && !isTrialClient && <span>✗</span>}
                            {participant.name}
                          </Badge>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={() => setShowAddClientDialog(true)}
              disabled={isFull}
              className="flex items-center gap-2 w-full sm:w-auto"
              size={isMobile ? 'sm' : 'default'}
            >
              <Plus className="h-4 w-4" />
              {isFull ? t('calendar.sessionFull') : t('calendar.addClient')}
            </Button>

            {effectiveBookings.length > 0 && (
              <Button
                onClick={handleViewClients}
                variant="outline"
                className="flex items-center gap-2 w-full sm:w-auto"
                size={isMobile ? 'sm' : 'default'}
              >
                <Users className="h-4 w-4" />
                {t('calendar.viewClientsForSession')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add Client Dialog */}
      <AddClientDialog
        open={showAddClientDialog}
        onOpenChange={setShowAddClientDialog}
        timeSlot={timeSlot}
        date={selectedDate}
        onClientAdded={handleAddClientSuccess}
        workoutType={workoutType}
      />

      {/* View Clients Dialog */}
      <EffectiveViewClientsDialog
        open={showViewClientsDialog}
        onOpenChange={setShowViewClientsDialog}
        timeSlot={timeSlot}
        selectedDate={selectedDate}
        workoutType={workoutType}
        effectiveBookings={effectiveBookings}
      />
    </>
  );
};
function normalizeTime(time: string) {
  return time.substring(0, 5);
}

export default EffectiveTimeSlotCard;
